/**
 * SOAP order cache: loading, freshness rules and invalidation
 * (the data lives in slices/soapOrderCacheSlice.js).
 *
 * A cached order or paid-initial answer is used only while it can't be out of date:
 *   - while this tab's live-update stream (the FrontDesk refresh events every order
 *     write sends) is connected, for answers cached since the cache started listening
 *     to it, and for at most SOAP_ORDER_MAX_AGE_MS (a stream that stalls without
 *     disconnecting delivers nothing): an 'ordered' event drops the orders of the days
 *     it names (all of them if it names none), a 'shopify' event drops the paid-initial
 *     answers, and connecting or disconnecting drops everything, since events sent in
 *     between were missed;
 *   - otherwise only for SOAP_ORDER_FRESH_MS after it was loaded, long enough to hand
 *     the order that came with the note to the note view;
 *   - any write this tab sends that names a cached order, or one on its way (in its
 *     URL, query or body), drops that order and stops later loads from joining the
 *     request sent before the write; the SOAP note view drops it after edits that
 *     don't name it (date, custom name, appointment type) and when it opens an order popup;
 *   - a response asked for before any of the above happened is not cached.
 *
 * Every read returns a copy, so the view can't change the cached order in place.
 */

import frontDeskWebSocket from '@/utils/frontDeskWebSocketClient';
import { onApiMutation, requestPath } from '@/utils/apiMutationListener';
import { apiClient } from './api/config';
import { checkInitialAppointmentPaid } from './api/soapNoteApi';
import {
  cacheSoapOrder,
  invalidateSoapOrder,
  cacheSoapPaidInitial,
  clearSoapOrderCache,
  applySoapOrderLiveRefresh,
  paidInitialKey,
} from './slices/soapOrderCacheSlice';

export const SOAP_ORDER_FRESH_MS = 5000;
// The longest any answer is used, even while the live-update stream reports connected
export const SOAP_ORDER_MAX_AGE_MS = 60 * 1000;

const copy = (value) => {
  if (value === null || value === undefined) return value;
  if (typeof structuredClone === 'function') return structuredClone(value);
  return JSON.parse(JSON.stringify(value));
};

const cacheState = (getState) => getState()?.soapOrderCache || {};

/** Change whenever orders / paid-initial answers are dropped; see the slice. */
export const getSoapOrderCacheEpoch = (getState) => cacheState(getState).orderEpoch || 0;
export const getSoapPaidInitialEpoch = (getState) => cacheState(getState).paidEpoch || 0;

let syncDispatch = null;
let syncStartedAt = null; // when the cache started listening to live updates and writes

const isLiveSyncConnected = () => Boolean(syncDispatch && frontDeskWebSocket.isConnected);

// Kept in step with live updates only if cached after the cache started listening:
// events sent before then were never seen.
const isUsable = (entry, now) => {
  if (!entry) return false;
  const age = now - entry.cachedAt;
  if (age <= SOAP_ORDER_FRESH_MS) return true;
  return isLiveSyncConnected() && entry.cachedAt >= syncStartedAt && age <= SOAP_ORDER_MAX_AGE_MS;
};

const ordersInFlight = new Map(); // orderId -> Promise of the response data
const paidInFlight = new Map(); // key -> Promise

/**
 * Connects the cache to live updates and to this tab's writes. Safe to call often;
 * only the first call (with a dispatch) does anything.
 */
export const ensureSoapOrderCacheSync = (dispatch) => {
  if (syncDispatch || typeof dispatch !== 'function') return;
  syncDispatch = dispatch;
  syncStartedAt = Date.now();

  frontDeskWebSocket.onRefresh((data) => {
    const sources = data?.sources || (data?.source ? [data.source] : []);
    dispatch(applySoapOrderLiveRefresh({ sources, dates: Array.isArray(data?.dates) ? data.dates : null }));
  });
  frontDeskWebSocket.onConnect(() => dispatch(clearSoapOrderCache()));
  frontDeskWebSocket.onDisconnect(() => dispatch(clearSoapOrderCache()));

  onApiMutation((config) => {
    // Saving the SOAP note sends its treatmentId but never writes the order
    if (requestPath(config) === '/soap-note') return;
    let haystack = String(config?.url || '');
    if (config?.params) haystack += ` ${JSON.stringify(config.params)}`;
    if (typeof config?.data === 'string') haystack += ` ${config.data}`;
    dispatch((innerDispatch, getState) => {
      const orderIds = new Set([...Object.keys(cacheState(getState).ordersById || {}), ...ordersInFlight.keys()]);
      [...orderIds]
        .filter((orderId) => haystack.includes(orderId))
        .forEach((orderId) => forgetSoapOrder(innerDispatch, orderId));
    });
  });
};

/** The cached order (a copy) when it is usable, else null. */
export const readCachedSoapOrder = (getState, orderId, now = Date.now()) => {
  if (!orderId) return null;
  const entry = cacheState(getState).ordersById?.[String(orderId)];
  return isUsable(entry, now) ? copy(entry.order) : null;
};

/**
 * GET /orderget/:id and cache the answer. Resolves to a copy of the order.
 * `joinInFlight` reuses a request for the same order that is already on its way
 * (opening a note); after an edit pass false so the answer is read after the edit.
 */
export const loadSoapOrder = (dispatch, getState, orderId, { joinInFlight = true } = {}) => {
  ensureSoapOrderCacheSync(dispatch);
  const id = String(orderId);
  if (joinInFlight && ordersInFlight.has(id)) return ordersInFlight.get(id).then(copy);

  const startEpoch = getSoapOrderCacheEpoch(getState);
  const request = apiClient.get(`/orderget/${id}`).then((res) => {
    const order = res.data;
    if (order?._id && getSoapOrderCacheEpoch(getState) === startEpoch) {
      dispatch(cacheSoapOrder(copy(order)));
    }
    return order;
  });
  const shared = request.finally(() => {
    if (ordersInFlight.get(id) === shared) ordersInFlight.delete(id);
  });
  ordersInFlight.set(id, shared);
  return shared.then(copy);
};

/** Stores an order the view just received from /orderget (e.g. for the Vitals popup). */
export const rememberSoapOrder = (dispatch, order) => {
  if (order?._id) dispatch(cacheSoapOrder(copy(order)));
};

/**
 * Drops an order that was (or may have been) edited, and all paid-initial answers.
 * Requests sent before are no longer joined, so the next load reads the edit.
 */
export const forgetSoapOrder = (dispatch, orderId) => {
  if (!orderId) return;
  ordersInFlight.delete(String(orderId));
  paidInFlight.clear();
  dispatch(invalidateSoapOrder(String(orderId)));
};

/**
 * GET /check-initial-appointment/:patientId, answered from the cache when it is
 * usable. Resolves to { hasPaidInitialAppointment, ... } like the request.
 */
export const getPaidInitialAppointment = (dispatch, getState, patientId, { currentOrderId, currentDate } = {}) => {
  ensureSoapOrderCacheSync(dispatch);
  const key = paidInitialKey(patientId, currentOrderId, currentDate);
  const entry = cacheState(getState).paidInitialByKey?.[key];
  if (isUsable(entry, Date.now())) {
    return Promise.resolve({ hasPaidInitialAppointment: entry.value, patientId });
  }
  if (paidInFlight.has(key)) return paidInFlight.get(key);

  const startEpoch = getSoapPaidInitialEpoch(getState);
  const request = checkInitialAppointmentPaid(patientId, { currentOrderId, currentDate })
    .then((result) => {
      if (typeof result?.hasPaidInitialAppointment === 'boolean' && getSoapPaidInitialEpoch(getState) === startEpoch) {
        dispatch(cacheSoapPaidInitial({ key, value: result.hasPaidInitialAppointment }));
      }
      return result;
    });
  const shared = request.finally(() => {
    if (paidInFlight.get(key) === shared) paidInFlight.delete(key);
  });
  paidInFlight.set(key, shared);
  return shared;
};
